"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useState } from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  id: "01",
  label: "PASSENGER INFO"
}, {
  id: "02",
  label: "TRIP DETAILS"
}, {
  id: "03",
  label: "LOCATION DATA"
}, {
  id: "04",
  label: "SCHEDULE"
}];
const included = ["Professional chauffeur", "Luxury vehicle selection", "Flight tracking included", "Complimentary water & amenities"];
const projectTypes = ["Airport Transfer", "Hourly Charter", "Corporate Travel", "Special Event", "Other"];
const budgetRanges = ["Standard Sedan", "Luxury SUV", "Executive Sprinter", "Group Transportation"];
const timelines = ["Immediate", "Within 24 hours", "Next week", "Next month"];
const serviceOptions = ["LAX Airport Pickup", "Private Aviation Transfer", "Point-to-Point", "Hourly Booking", "Wedding Service", "Business Meetings"];
const timeSlots = ["08:00 — 09:00", "10:00 — 11:00", "13:00 — 14:00", "15:00 — 16:00", "18:00 — 19:00"];
function StepIndicator({
  current
}: {
  current: number;
}) {
  return <div className="space-y-3">
      {steps.map((step, i) => {
      const isActive = i === current;
      return <div key={step.id} data-index={i} className={`flex items-center gap-4 font-sans text-xs tracking-widest uppercase ${isActive ? "text-primary" : "text-muted-foreground"}`}>
            <span className={`inline-block w-8 h-px ${isActive ? "bg-primary" : "bg-muted"}`} />
            <span>{step.id}/04</span>
            <span>{step.label}</span>
          </div>;
    })}
    </div>;
}
function MultiStepForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [step, setStep] = useState(0);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const progress = (step + 1) / 4 * 100;
  const toggleService = (svc: string) => {
    setSelectedServices(prev => prev.includes(svc) ? prev.filter(s => s !== svc) : [...prev, svc]);
  };
  if (isSubmitted && !submitError) {
    return <div className="border border-border p-10 bg-white/60 backdrop-blur-md rounded-sm">
        <p className="font-sans text-xs tracking-widest uppercase text-primary mb-4">
          Reservation Received
        </p>
        <h3 className="font-serif text-3xl text-foreground mb-4">Thank you.</h3>
        <p className="font-body text-foreground/80 mb-6 italic">{successMessage}</p>
        <Button onClick={resetForm} className="btn-primary rounded-sm">
          Book Another Trip
        </Button>
      </div>;
  }
  const underlineInputClass = "h-12 px-0 text-lg bg-transparent border-0 border-b border-border rounded-none focus-visible:border-primary focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none transition-colors";
  const labelClass = "font-sans text-xs tracking-widest uppercase text-muted-foreground";
  return <Form onSubmit={e => {
    console.log("Form Submission Values:", control._formValues);
    handleSubmit(e);
  }} className="space-y-12">
      {step === 0 && <div className="space-y-10">
          <div className="space-y-2">
            <p className="font-sans text-xs tracking-widest uppercase text-primary">
              Step 01 — Passenger Info
            </p>
            <h3 className="font-serif text-3xl md:text-4xl text-foreground">
              Passenger Details
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <FormField control={control} name="full_name" rules={{
          required: "Full name is required",
          minLength: {
            value: 2,
            message: "Please enter your full name"
          }
        }} render={({
          field
        }) => <FormItem>
                  <FormLabel className={labelClass}>Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} className={underlineInputClass} />
                  </FormControl>
                  <FormMessage className="italic text-red-600 font-body text-sm" />
                </FormItem>} />

            <FormField control={control} name="email_address" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email"
          }
        }} render={({
          field
        }) => <FormItem>
                  <FormLabel className={labelClass}>Email Address</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="phoenixenerprises@gmail.com" {...field} className={underlineInputClass} />
                  </FormControl>
                  <FormMessage className="italic text-red-600 font-body text-sm" />
                </FormItem>} />

            <FormField control={control} name="phone_number" rules={{
          required: "Phone number is required"
        }} render={({
          field
        }) => <FormItem>
                  <FormLabel className={labelClass}>Phone Number</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="323-492-6292" {...field} className={underlineInputClass} />
                  </FormControl>
                  <FormMessage className="italic text-red-600 font-body text-sm" />
                </FormItem>} />

            <FormField control={control} name="company" render={({
          field
        }) => <FormItem>
                  <FormLabel className={labelClass}>Company (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Company name" {...field} className={underlineInputClass} />
                  </FormControl>
                  <FormMessage className="italic text-red-600 font-body text-sm" />
                </FormItem>} />
          </div>
        </div>}

      {step === 1 && <div className="space-y-10">
          <div className="space-y-2">
            <p className="font-sans text-xs tracking-widest uppercase text-primary">
              Step 02 — Trip Details
            </p>
            <h3 className="font-serif text-3xl md:text-4xl text-foreground">
              Service Requirements
            </h3>
          </div>

          <FormField control={control} name="message" render={({
        field
      }) => <FormItem>
                <FormLabel className={labelClass}>Special Instructions</FormLabel>
                <FormControl>
                  <Textarea placeholder="Flight number, luggage count, or special requests..." {...field} className="min-h-32 bg-transparent border-0 border-b border-border focus-visible:border-primary focus-visible:ring-0 focus-visible:ring-offset-0 text-lg px-0" />
                </FormControl>
                <FormMessage className="italic text-red-600 font-body text-sm" />
              </FormItem>} />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FormField control={control} name="project_type" rules={{
          required: "Please select a service type"
        }} render={({
          field
        }) => <FormItem>
                  <FormLabel className={labelClass}>Service Type</FormLabel>
                  <FormControl>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger className="h-12 px-0 bg-transparent border-0 border-b border-border text-lg focus-visible:border-primary focus-visible:ring-0">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {projectTypes.map((t, i) => <SelectItem key={t} value={t} data-index={i}>
                            {t}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage className="italic text-red-600 font-body text-sm" />
                </FormItem>} />

            <FormField control={control} name="budget_range" render={({
          field
        }) => <FormItem>
                  <FormLabel className={labelClass}>Vehicle Class</FormLabel>
                  <FormControl>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger className="h-12 px-0 bg-transparent border-0 border-b border-border text-lg focus-visible:border-primary focus-visible:ring-0">
                        <SelectValue placeholder="Select vehicle" />
                      </SelectTrigger>
                      <SelectContent>
                        {budgetRanges.map((b, i) => <SelectItem key={b} value={b} data-index={i}>
                            {b}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage className="italic text-red-600 font-body text-sm" />
                </FormItem>} />

            <FormField control={control} name="timeline" rules={{
          required: "Please select urgency"
        }} render={({
          field
        }) => <FormItem>
                  <FormLabel className={labelClass}>Urgency</FormLabel>
                  <FormControl>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger className="h-12 px-0 bg-transparent border-0 border-b border-border text-lg focus-visible:border-primary focus-visible:ring-0">
                        <SelectValue placeholder="Select urgency" />
                      </SelectTrigger>
                      <SelectContent>
                        {timelines.map((t, i) => <SelectItem key={t} value={t} data-index={i}>
                            {t}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage className="italic text-red-600 font-body text-sm" />
                </FormItem>} />
          </div>

          <div>
            <p className={`${labelClass} mb-4`}>Service Options</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {serviceOptions.map((svc, i) => {
            const active = selectedServices.includes(svc);
            return <Button key={svc} type="button" data-index={i} onClick={() => toggleService(svc)} variant="outline" className={`flex items-center justify-start gap-3 px-4 py-3 text-left font-body text-sm transition-colors rounded-sm h-auto ${active ? "border-primary bg-primary/10 text-primary" : "border-border text-foreground hover:border-primary/50"}`}>
                    <span className={`flex items-center justify-center w-4 h-4 border rounded-sm shrink-0 ${active ? "bg-primary border-primary" : "border-border"}`}>
                      {active && <Check className="w-3 h-3 text-white" />}
                    </span>
                    {svc}
                  </Button>;
          })}
            </div>
          </div>
        </div>}

      {step === 2 && <div className="space-y-10">
          <div className="space-y-2">
            <p className="font-sans text-xs tracking-widest uppercase text-primary">
              Step 03 — Location Data
            </p>
            <h3 className="font-serif text-3xl md:text-4xl text-foreground">
              Where to meet?
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <FormField control={control} name="location" rules={{
          required: "Pickup address is required"
        }} render={({
          field
        }) => <FormItem>
                  <FormLabel className={labelClass}>Pickup Address</FormLabel>
                  <FormControl>
                    <Input placeholder="Street, City, Zip" {...field} className={underlineInputClass} />
                  </FormControl>
                  <FormMessage className="italic text-red-600 font-body text-sm" />
                </FormItem>} />

            <FormField control={control} name="lot_size" rules={{
          required: "Drop-off address is required"
        }} render={({
          field
        }) => <FormItem>
                  <FormLabel className={labelClass}>Drop-off Address</FormLabel>
                  <FormControl>
                    <Input placeholder="Street, City, Zip" {...field} className={underlineInputClass} />
                  </FormControl>
                  <FormMessage className="italic text-red-600 font-body text-sm" />
                </FormItem>} />

            <FormField control={control} name="site_documents" render={({
          field: {
            value,
            onChange,
            ...field
          }
        }) => <FormItem className="md:col-span-2">
                  <FormLabel className={labelClass}>Supporting Documents (Optional)</FormLabel>
                  <FormControl>
                    <Input type="file" className={underlineInputClass} onChange={e => onChange(e.target.files?.[0] || "")} {...field} />
                  </FormControl>
                  <FormMessage className="italic text-red-600 font-body text-sm" />
                </FormItem>} />
          </div>
        </div>}

      {step === 3 && <div className="space-y-10">
          <div className="space-y-2">
            <p className="font-sans text-xs tracking-widest uppercase text-primary">
              Step 04 — Schedule
            </p>
            <h3 className="font-serif text-3xl md:text-4xl text-foreground">
              Select Date & Time
            </h3>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="border border-border p-4 bg-white/50 rounded-sm">
              <FormField control={control} name="preferred_date" rules={{
            required: "Please select a date"
          }} render={({
            field
          }) => <FormItem>
                    <FormControl>
                      <SafeHydrate><Calendar mode="single" selected={selectedDate} onSelect={d => {
                  setSelectedDate(d);
                  field.onChange(d ? d.toISOString().split("T")[0] : "");
                }} className="w-full" /></SafeHydrate>
                    </FormControl>
                    <FormMessage className="italic text-red-600 font-body text-sm" />
                  </FormItem>} />
            </div>

            <div className="space-y-4">
              <p className={labelClass}>Available Time Slots</p>
              <FormField control={control} name="time_slot" rules={{
            required: "Please select a time slot"
          }} render={({
            field
          }) => <FormItem>
                    <FormControl>
                      <div className="space-y-2">
                        {timeSlots.map((slot, i) => {
                  const active = selectedSlot === slot;
                  return <Button key={slot} type="button" data-index={i} onClick={() => {
                    setSelectedSlot(slot);
                    field.onChange(slot);
                  }} variant={active ? "default" : "outline"} className={`w-full justify-start font-sans text-sm tracking-wider rounded-sm h-auto px-5 py-3 ${active ? "bg-primary text-white border-primary" : "border-border text-foreground hover:border-primary/60 hover:bg-white"}`}>
                              {slot}
                            </Button>;
                })}
                      </div>
                    </FormControl>
                    <FormMessage className="italic text-red-600 font-body text-sm" />
                  </FormItem>} />
            </div>
          </div>
        </div>}

      {submitError && <div className="p-4 border border-red-600 bg-red-50 rounded-sm">
          <p className="text-red-600 italic font-body">{submitError}</p>
        </div>}

      <div className="flex items-center justify-between pt-8 border-t border-border">
        {step > 0 ? <Button type="button" variant="ghost" onClick={() => setStep(step - 1)} className="font-sans text-xs tracking-widest uppercase rounded-sm">
            Previous
          </Button> : <span />}

        {step < 3 ? <Button type="button" onClick={() => setStep(step + 1)} className="btn-primary rounded-sm px-6 py-3 h-auto">
            Next
          </Button> : <Button type="submit" disabled={isSubmitting || isSubmitted} className="btn-primary rounded-sm px-6 py-3 h-auto">
            {isSubmitting ? "Submitting..." : "Confirm Reservation"}
          </Button>}
      </div>

      <div className="space-y-2 pt-2">
        <div className="flex justify-between font-sans text-xs tracking-widest uppercase text-muted-foreground">
          <span>Progress</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="h-px bg-border" />
      </div>
    </Form>;
}
export default function Bookingform() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const [currentStep] = useState(0);
  return <section data-section-id="3393" id="bookingform" className="bg-background py-32 px-6 md:px-12">
      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-32 space-y-12">
              <div>
                <p className="font-sans text-xs tracking-widest uppercase text-primary mb-6">
                  Secure Your Ride
                </p>
                <h2 className="font-serif text-5xl md:text-6xl text-foreground leading-tight mb-6">
                  Luxury <br />
                  <em className="italic text-primary">Reservations.</em>
                </h2>
                <p className="font-body text-lg text-foreground/70 italic leading-relaxed max-w-md">
                  Experience premium black car service in Los Angeles. 
                  Provide your details below to secure your professional chauffeur.
                </p>
              </div>

              <StepIndicator current={currentStep} />

              <div className="border-t border-border pt-8">
                <p className="font-sans text-xs tracking-widest uppercase text-muted-foreground mb-5">
                  What is Included
                </p>
                <ul className="space-y-3">
                  {included.map((item, i) => <li key={item} data-index={i} className="flex items-start gap-3 font-body text-foreground">
                      <span className="font-sans text-xs text-primary mt-1.5 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span>{item}</span>
                    </li>)}
                </ul>
              </div>

              <div className="border-t border-border pt-8 space-y-2">
                <p className="font-sans text-xs tracking-widest uppercase text-muted-foreground">
                  Direct Contact
                </p>
                <p className="font-body text-foreground">phoenixenerprises@gmail.com</p>
                <p className="font-body text-foreground">323-492-6292</p>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-7">
            <FormProvider formKey="booking_form_master" formId="booking_form_master" sectionName="bookingform" defaultValues={{
            full_name: "",
            email_address: "",
            phone_number: "",
            company: "",
            message: "",
            project_type: "",
            budget_range: "",
            timeline: "",
            location: "",
            lot_size: "",
            site_documents: "",
            preferred_date: "",
            time_slot: ""
          }} validationRules={{
            full_name: {
              required: true,
              minLength: 2
            },
            email_address: {
              required: true,
              pattern: "email"
            },
            phone_number: {
              required: true,
              pattern: "phone"
            },
            project_type: {
              required: true
            },
            timeline: {
              required: true
            },
            location: {
              required: true
            },
            lot_size: {
              required: true
            },
            preferred_date: {
              required: true
            },
            time_slot: {
              required: true
            }
          }} submitText="Confirm Reservation" successMessage="Thank you! Your booking request has been received. Phoenix Enterprises Limousine Service will contact you shortly to confirm your reservation." errorMessage="We were unable to process your request. Please try again or call us directly.">
              <MultiStepForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}